/**
 * FIN-11304 — the header card's right-hand metrics: Variants Breached, the
 * per-action counts, and the Alert Score.
 *
 * SAME SOURCE AS CLASSIC, deliberately. `SmartAssessmentTopPanel` builds its two
 * counter tiles by JSON-parsing `triggeredRulesActionSummary` (a map of action ->
 * count, e.g. `{"REJECTED": 1}`), summing the values for "Variants Breached" and
 * emitting one tile per key. Reproducing that here rather than inventing a second
 * derivation keeps the two views from ever disagreeing about the same alert — if
 * Classic says 2 breached, Spatial must not say 1.
 *
 * Parsing lives in its own module so the arithmetic is testable without rendering,
 * and so the malformed-JSON case has one obvious home: the field is a STRING on
 * the wire, so a bad value must not take the header down with it.
 */

/**
 * @param {object} alertDetails raw alert payload (amlsCommon.alertDetails)
 * @returns {{variantsBreached:number, actions:Array<{name:string,count:number}>,
 *            alertScore:?number, alertLevel:?string, alertLevelLabel:?string,
 *            alertLevelColor:?string}}
 */
export const toMetrics = (alertDetails) => {
  const summary = alertDetails?.triggeredRulesActionSummary;

  let counts = {};
  try {
    // A string on the wire, and only sometimes valid JSON — an unparseable value
    // must degrade to "no counters", never throw. The header card carries the
    // alert id and status controls; losing those to a bad counter string would
    // cost far more than the tiles are worth.
    const parsed = typeof summary === 'string' ? JSON.parse(summary) : summary;
    if (parsed && typeof parsed === 'object') counts = parsed;
  } catch (e) {
    counts = {};
  }

  const actions = Object.keys(counts)
    .filter((name) => typeof counts[name] === 'number')
    .map((name) => ({ name, count: counts[name] }));

  return {
    // The TOTAL across actions, matching Classic's `totalOfSampleData`. Not the
    // number of distinct actions: two rules both rejecting is 2 variants breached,
    // not 1.
    variantsBreached: actions.reduce((sum, a) => sum + a.count, 0),
    actions,
    // Absent on live data today (the alert payload carries no alertScore at all),
    // which is why Classic's score block is invisible there too. Passed through
    // rather than defaulted: 0 is a real score and must not stand in for "unknown".
    alertScore: alertDetails?.alertScore,
    alertLevel: alertDetails?.alertLevel,
    alertLevelLabel: alertDetails?.alertLevelLabel,
    /* THE TENANT'S OWN COLOUR FOR THIS LEVEL (FIN-14138). Classic reads this
       same field — `SmartAssessmentTopPanel:539` passes it straight to the
       Progress `strokeColor` — and so do NS, Alert History and the shared
       `common/components/AlertScore`. It comes from `alert_level_details`
       (code / display_name / COLOR), so the band's hue is configuration, not a
       constant the frontend gets to choose. Spatial reading anything else is
       how the two views came to disagree about one alert. */
    alertLevelColor: alertDetails?.alertLevelColor
  };
};
